import Phaser from 'phaser';
import { SCENE_KEYS, HEADER_CARD_GAP } from '@/config/constants';
import { submitStageScore } from '@/utils/SupabaseClient';
import { AudioManager } from '@/utils/AudioManager';
import { transitionTo, fadeIn } from '@/utils/UIAnimations';
import { PostFXManager } from '@/utils/PostFXManager';
import { BackgroundManager } from '@/utils/BackgroundManager';
import { COLORS, COLORS_HEX, FONTS } from '@/ui/theme';
import { createPillButton } from '@/ui/PillButton';
import { drawSelectionHalo } from '@/ui/SelectionHalo';
import { GamepadNav } from '@/utils/GamepadNav';
import { templateCardText } from '@/utils/CardText';
import { NarrativeCardModal } from '@/ui/NarrativeCardModal';
import { WORLD_BRIDGES } from '@/utils/NarrativeCards';

/**
 * Stage Clear Scene - chunky cartoon redesign.
 *
 *   ┌──────────────────────────────────────────────────┐
 *   │                  HELMEPA                         │
 *   │              POSEIDON CHALLENGE                  │
 *   │  ╭──────────────────────────────────────────╮    │
 *   │  │       STAGE 1 CLEARED  (green)           │    │
 *   │  │  ──────────────────────────────────────  │    │
 *   │  │      TOTAL SCORE 1,780  (yellow)         │    │
 *   │  │   SCORE   |  TIME LEFT |  TIME BONUS     │    │
 *   │  │   1,000   |    78s     |     780         │    │
 *   │  │     [REPLAY STAGE]   [NEXT STAGE]        │    │
 *   │  ╰──────────────────────────────────────────╯    │
 *   └──────────────────────────────────────────────────┘
 */
export class StageClearScene extends Phaser.Scene {
  private stageNumber = 1;
  private score = 0;
  private startingScore = 0;
  private timeRemaining = 0;
  private timeBonus = 0;
  private totalScore = 0;       // cumulative score (post-bonus) - leaderboard + carry-forward
  private stageScore = 0;       // points earned this stage only (no carry-in)
  private stageTotal = 0;       // stageScore + timeBonus - what the on-screen totals add up to
  private bagsCleared = 0;
  private creatureName = '';
  private outroHeadline = '';
  private outroBody = '';
  private isTransitioning = false;
  private nav?: GamepadNav;
  private stageBackground: any = null;
  private cardLayer: Phaser.GameObjects.GameObject[] = [];
  private halo?: Phaser.GameObjects.Graphics;
  private drawHaloHandler?: () => void;

  constructor() {
    super({ key: SCENE_KEYS.STAGE_CLEAR });
  }

  init(data: {
    stage: number;
    score: number;
    startingScore: number;
    timeRemaining: number;
    bagsCleared?: number;
    educationalOutro?: { headline: string; body: string };
    creatureName?: string;
    background?: any;
  }): void {
    this.stageNumber = data.stage || 1;
    this.score = data.score || 0;
    this.startingScore = data.startingScore || 0;
    this.timeRemaining = data.timeRemaining || 0;
    this.timeBonus = this.timeRemaining * 10;
    this.stageScore = Math.max(0, this.score - this.startingScore);
    this.stageTotal = this.stageScore + this.timeBonus;
    this.totalScore = this.score + this.timeBonus;
    this.stageBackground = data.background || null;
    this.bagsCleared = data.bagsCleared || 0;
    this.creatureName = data.creatureName || '';
    this.outroHeadline = data.educationalOutro?.headline || '';
    this.outroBody = data.educationalOutro?.body || '';

    console.log(`🎉 Stage ${this.stageNumber} clear - stage ${this.stageScore} + bonus ${this.timeBonus} = stage total ${this.stageTotal} | running total ${this.totalScore}`);
  }

  create(): void {
    const { width, height } = this.cameras.main;
    const cx = width / 2;
    this.isTransitioning = false;
    this.input.enabled = true;
    this.cardLayer = [];

    fadeIn(this, 400);
    PostFXManager.attach(this, 'stageClear');

    // Background - reuse the just-cleared stage's background so the celebration
    // happens "in place" instead of teleporting back to world 1. Falls back to
    // the menu's world1 video only if the cleared stage didn't ship a config.
    const bgManager = new BackgroundManager(this);
    bgManager.createBackground(this.stageBackground || {
      type: 'video',
      video: 'world1 video',
      scaleMode: 'cover',
    });
    const scrim = this.add.graphics().setDepth(1);
    scrim.fillStyle(0x020b18, 0.40).fillRect(0, 0, width, height);

    // Audio fanfare
    const am = AudioManager.getInstance();
    const sfx = this.cache.audio.exists('sfx_stage_clear')
      ? 'sfx_stage_clear'
      : (this.cache.audio.exists('sfx_fanfare_common') ? 'sfx_fanfare_common' : 'sfx_star_earn');
    am.playSFX(this, sfx, { volume: 0.7 });

    // Record this stage result for the per-stage leaderboard. This writes to
    // the dedicated stage_scores table, NOT the run-level `scores` table, so
    // the CSV / top_scores stay one row per run.
    void this.submitStageResult();

    // ── HEADER ────────────────────────────────────────────────────────
    if (this.textures.exists('helmepa_logo')) {
      const h = this.add.image(cx, 88, 'helmepa_logo')
        .setOrigin(0.5).setDepth(10).setAlpha(0.95);
      h.setScale(162 / h.width);
    }
    let titleBottom = 268.6; // fallback if title texture is missing
    if (this.textures.exists('title_logo_v4')) {
      const t = this.add.image(cx, 200, 'title_logo_v4').setOrigin(0.5).setDepth(10);
      t.setScale(378 / t.width);
      titleBottom = t.y + t.displayHeight / 2;
    }

    // ── PANEL ─────────────────────────────────────────────────────────
    const hasOutro = !!(this.outroHeadline || this.outroBody);
    const pw = 1200;
    const ph = 720;
    const panelTop = Math.round(titleBottom + HEADER_CARD_GAP);
    const panelLeft = cx - pw / 2;
    const panelRadius = 32;

    const panel = this.add.graphics().setDepth(2);
    panel.fillStyle(COLORS.deepOcean, 0.55);
    panel.fillRoundedRect(panelLeft, panelTop, pw, ph, panelRadius);
    panel.lineStyle(2, COLORS.aqua, 0.70);
    panel.strokeRoundedRect(panelLeft, panelTop, pw, ph, panelRadius);
    this.cardLayer.push(panel);

    // ── STAGE N CLEARED title (Skranji, vibrant green) ────────────────
    // Smaller when the outro is shown so the panel doesn't feel cramped.
    const titleY = panelTop + (hasOutro ? 90 : 130);
    const titleSize = hasOutro ? '72px' : '108px';
    const titleText = this.add.text(cx, titleY, `STAGE ${this.stageNumber} CLEARED`, {
      fontFamily: FONTS.fancyRegular,
      fontSize: titleSize,
      color: '#7fff3a',
      stroke: '#0c2a08',
      strokeThickness: 6,
      shadow: { offsetX: 0, offsetY: 6, color: '#000000', blur: 14, fill: true },
    }).setOrigin(0.5).setDepth(10);
    titleText.setScale(0.6);
    this.cardLayer.push(titleText);
    this.tweens.add({
      targets: titleText, scale: 1, duration: 320, ease: 'Back.easeOut', delay: 120,
    });

    // ── Score block ───────────────────────────────────────────────────
    if (hasOutro) {
      // Mission-complete card (stages 2 / 4 / 6): MISSION N COMPLETE headline
      // + run tally + divider + TOTAL SCORE, centred as one balanced block in
      // the space between the title and the buttons. Deliberately NO flavour
      // line and NO SCORE / TIME LEFT / TIME BONUS columns.
      const headline = templateCardText(this, this.outroHeadline);
      // Keep only the tally (first paragraph); drop the flavour line that
      // follows the blank line in the outro body.
      const tally = this.expandOutroTokens(templateCardText(this, this.outroBody)).split('\n\n')[0];

      const hl = headline
        ? this.add.text(cx, 0, headline, {
            fontFamily: FONTS.display,
            fontSize: '40px',
            color: COLORS_HEX.aquaLight,
            stroke: COLORS_HEX.midnightDepth,
            strokeThickness: 3,
            align: 'center',
            shadow: { offsetX: 0, offsetY: 3, color: '#000000', blur: 8, fill: true },
          }).setOrigin(0.5).setDepth(10)
        : null;
      if (hl) this.cardLayer.push(hl);

      const tallyText = tally
        ? this.add.text(cx, 0, tally, {
            fontFamily: FONTS.displayLight,
            fontSize: '34px',
            color: COLORS_HEX.textPrimary,
            align: 'center',
            lineSpacing: 12,
            shadow: { offsetX: 0, offsetY: 2, color: '#000000', blur: 4, fill: true },
          }).setOrigin(0.5).setDepth(10)
        : null;
      if (tallyText) this.cardLayer.push(tallyText);

      const totalText = this.add.text(cx, 0, `TOTAL SCORE ${this.stageTotal.toLocaleString()}`, {
        fontFamily: FONTS.fancyRegular,
        fontSize: '64px',
        color: '#ffe000',
        stroke: '#3a2a00',
        strokeThickness: 5,
        shadow: { offsetX: 0, offsetY: 4, color: '#000000', blur: 8, fill: true },
      }).setOrigin(0.5).setDepth(10);
      this.cardLayer.push(totalText);

      // Stack headline → tally → divider → TOTAL with even gaps, then centre
      // the whole block vertically between the title and the button row.
      const gapHeadlineTally = 30;
      const gapTallyDivider = 46;
      const gapDividerTotal = 46;
      const dividerH = 2;
      const hHeadline = hl ? hl.height : 0;
      const hTally = tallyText ? tallyText.height : 0;
      const blockH = hHeadline + (hl ? gapHeadlineTally : 0)
        + hTally + gapTallyDivider + dividerH + gapDividerTotal + totalText.height;

      const areaTop = titleY + titleText.height / 2 + 48;
      const areaBottom = (panelTop + ph - 100) - 55 - 48; // clear of the button row
      let cy = areaTop + Math.max((areaBottom - areaTop - blockH) / 2, 0);

      if (hl) { hl.setY(cy + hHeadline / 2); cy += hHeadline + gapHeadlineTally; }
      if (tallyText) { tallyText.setY(cy + hTally / 2); cy += hTally + gapTallyDivider; }

      const divY = cy + dividerH / 2;
      const div = this.add.graphics().setDepth(10);
      div.lineStyle(2, COLORS.aqua, 0.45);
      div.lineBetween(panelLeft + 110, divY, panelLeft + pw - 110, divY);
      this.cardLayer.push(div);
      cy += dividerH + gapDividerTotal;

      totalText.setY(cy + totalText.height / 2);
    } else {
      // Regular stage-clear card (stages 1 / 3 / 5): divider + TOTAL SCORE +
      // SCORE / TIME LEFT / TIME BONUS columns. (Unchanged.)
      const divY = panelTop + 220;
      const div = this.add.graphics().setDepth(10);
      div.lineStyle(2, COLORS.aqua, 0.45);
      div.lineBetween(panelLeft + 80, divY, panelLeft + pw - 80, divY);
      this.cardLayer.push(div);

      this.cardLayer.push(this.add.text(cx, divY + 60, `TOTAL SCORE ${this.stageTotal.toLocaleString()}`, {
        fontFamily: FONTS.fancyRegular,
        fontSize: '76px',
        color: '#ffe000',
        stroke: '#3a2a00',
        strokeThickness: 5,
        shadow: { offsetX: 0, offsetY: 4, color: '#000000', blur: 8, fill: true },
      }).setOrigin(0.5).setDepth(10));

      const statY = divY + 175;
      const statSpacing = 320;
      const stats: Array<{ label: string; value: string }> = [
        { label: 'SCORE',      value: this.stageScore.toLocaleString() },
        { label: 'TIME LEFT',  value: `${this.timeRemaining}s` },
        { label: 'TIME BONUS', value: this.timeBonus.toLocaleString() },
      ];
      stats.forEach((s, i) => {
        const xPos = cx + (i - 1) * statSpacing;
        this.cardLayer.push(this.add.text(xPos, statY, s.label, {
          fontFamily: FONTS.displayHeavy,
          fontSize: '24px',
          color: COLORS_HEX.aquaLight,
          stroke: COLORS_HEX.midnightDepth,
          strokeThickness: 1,
        }).setOrigin(0.5).setDepth(10).setLetterSpacing(2));
        this.cardLayer.push(this.add.text(xPos, statY + 50, s.value, {
          fontFamily: FONTS.displayHeavy,
          fontSize: '52px',
          color: COLORS_HEX.textPrimary,
          stroke: COLORS_HEX.midnightDepth,
          strokeThickness: 3,
        }).setOrigin(0.5).setDepth(10));
      });
    }

    // ── BUTTON ROW - two PLAY-style rectangular chips (360 × 110, 56px).
    //    Both variants share the silhouette of the home-screen PLAY button.
    const btnY = panelTop + ph - 100;
    const btnW = 360;
    const gap = 60;
    const halfBlock = btnW / 2 + gap / 2;

    const isLastStage = this.stageNumber >= 6;
    const nextLabel = isLastStage ? 'BOSS LEVEL' : 'NEXT STAGE';

    const replayBtn = createPillButton(this, {
      x: cx - halfBlock,
      y: btnY,
      label: 'REPLAY STAGE',
      variant: 'secondary',
      fontSize: 44,
      onClick: () => this.handleReplay(),
    });
    replayBtn.setDepth(11);
    this.cardLayer.push(replayBtn);

    const nextBtn = createPillButton(this, {
      x: cx + halfBlock,
      y: btnY,
      label: nextLabel,
      variant: 'primary',
      fontSize: 44,
      onClick: () => this.handleNext(),
    });
    nextBtn.setDepth(11);
    this.cardLayer.push(nextBtn);

    // Focus halo - visible cyan ring around the SELECTED button.
    // Nothing is pre-selected (focusIdx = -1): the player must deliberately
    // move onto a CTA with the arrow keys / D-pad / mouse before ENTER/SPACE
    // can activate it. This stops a stray gameplay button-mash from instantly
    // triggering a CTA the moment the popup appears.
    const buttons = [replayBtn, nextBtn];
    let focusIdx = -1; // -1 = no selection yet
    const halo = this.add.graphics().setDepth(12);
    this.halo = halo;
    const drawHalo = () => {
      if (focusIdx < 0) { halo.clear(); return; } // nothing selected → no ring
      const b = buttons[focusIdx];
      // Offset the button's CURRENT (scaled) pill - using its real elliptical
      // cap radii - by an even gap, so the ring stays a parallel pill that grows
      // with the button on select.
      drawSelectionHalo(halo, {
        cx: b.x,
        cy: b.y,
        w: ((b as any).pillWidth ?? btnW) * b.scaleX,
        h: ((b as any).pillHeight ?? 110) * b.scaleY,
        rx: ((b as any).pillCapRx ?? 55) * b.scaleX,
        ry: ((b as any).pillCapRy ?? 55) * b.scaleY,
      }, { gap: 14 });
    };
    drawHalo();
    this.drawHaloHandler = drawHalo;
    // Redraw each frame so the ring tracks the button's grow-on-select tween.
    this.events.on(Phaser.Scenes.Events.UPDATE, drawHalo);
    replayBtn.on('pointerover', () => { focusIdx = 0; drawHalo(); });
    nextBtn.on('pointerover',   () => { focusIdx = 1; drawHalo(); });

    // Shared select/confirm helpers. `confirm` is a no-op until the player has
    // actually selected a CTA, so a held SPACE (the shoot key) can't advance.
    const select = (i: number) => { focusIdx = i; drawHalo(); };
    const confirm = () => { if (focusIdx >= 0) (buttons[focusIdx] as any).press?.(); };
    const fireReplay = () => (replayBtn as any).press?.();

    // Keyboard: ←/→ move the selection, ENTER/SPACE confirm the SELECTED CTA,
    // R is an explicit replay shortcut.
    const kb = this.input.keyboard;
    if (kb) {
      kb.on('keydown-LEFT',  () => select(0));
      kb.on('keydown-RIGHT', () => select(1));
      kb.on('keydown-ENTER', confirm);
      kb.on('keydown-SPACE', confirm);
      kb.on('keydown-R', fireReplay);
    }

    // Gamepad: D-pad/stick moves the selection, A and B both press the focused
    // CTA. No hard-wired shortcut from B, so you cannot fire a CTA that does
    // not have the focus frame on it. (Keyboard R still triggers replay.)
    this.nav = new GamepadNav(this, {
      onLeft:  () => select(0),
      onRight: () => select(1),
      onUp:    () => select(0),
      onDown:  () => select(1),
      onConfirm: confirm,
    });

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => bgManager.destroyBackground());
  }

  update(): void {
    this.nav?.update();
  }

  /**
   * Substitute stage-run tokens in the outro body. {bags} is the count of
   * popped item-bubbles this run, {creature} is the ambient creature label
   * pulled from the stage config (e.g. "turtles"). Player-name templating
   * is handled separately by templateCardText() upstream of this.
   */
  private expandOutroTokens(text: string): string {
    return text
      .replace(/\{bagsX1000\}/g, (this.bagsCleared * 1000).toLocaleString())
      .replace(/\{bags\}/g, this.bagsCleared.toLocaleString())
      .replace(/\{creature\}/g, this.creatureName || 'sea life');
  }

  private async submitStageResult(): Promise<void> {
    const playerId = this.registry.get('playerId');
    const playerName = this.registry.get('playerName');
    if (!playerName || !playerId) return;
    try {
      await submitStageScore({
        playerId,
        playerName,
        stageNumber: this.stageNumber,
        stageScore: this.stageScore, // points earned this stage only
        timeRemaining: this.timeRemaining,
      });
    } catch (e) {
      console.warn('Stage score submit failed:', e);
    }
  }

  private handleReplay(): void {
    if (this.isTransitioning) return;
    this.isTransitioning = true;
    transitionTo(this, SCENE_KEYS.GAME, { stage: this.stageNumber, score: this.startingScore }, 500, 0x000000, 'parallaxPush');
  }
  private handleNext(): void {
    if (this.isTransitioning) return;
    this.isTransitioning = true;

    const bridge = WORLD_BRIDGES[this.stageNumber];
    if (bridge) {
      // World boundary or pre-boss bridge: clear the stage-clear card off the
      // screen first so the "Next Mission" card replaces it instead of
      // overlapping it, then pop the narrative card. The bridge has its own
      // CTA so a second click is required to advance.
      this.hideClearCard();
      const modal = new NarrativeCardModal(this, bridge, () => this.advanceAfterBridge());
      modal.show();
      return;
    }
    this.advanceAfterBridge();
  }

  /**
   * Fade the stage-clear card (panel, score block, buttons, focus halo) out so
   * the between-mission NarrativeCardModal reads as a fresh card rather than
   * stacking on top of the clear screen. The header logos stay put: the modal
   * redraws them in the same spot, so they line up seamlessly.
   */
  private hideClearCard(): void {
    // Kill the focus ring for good: stop its per-frame redraw, stop the gamepad
    // nav that also redraws it on stick/d-pad input, then wipe and hide the
    // graphics so nothing can repaint it behind the bridge card.
    if (this.drawHaloHandler) {
      this.events.off(Phaser.Scenes.Events.UPDATE, this.drawHaloHandler);
      this.drawHaloHandler = undefined;
    }
    this.nav?.destroy();
    this.nav = undefined;
    if (this.halo) {
      this.halo.clear();
      this.halo.setVisible(false);
    }
    this.cardLayer.forEach((obj) => {
      const o = obj as any;
      o.disableInteractive?.();
      this.tweens.add({
        targets: obj,
        alpha: 0,
        duration: 150,
        ease: 'Sine.easeIn',
        onComplete: () => o.setVisible?.(false),
      });
    });
    // Safety net: even if a fade tween gets interrupted, force every card
    // element fully hidden so nothing (panel, score, button, focus ring) can
    // peek through the bridge card.
    this.time.delayedCall(180, () => {
      this.cardLayer.forEach((obj) => (obj as any).setVisible?.(false));
      this.halo?.setVisible(false);
    });
  }

  private advanceAfterBridge(): void {
    if (this.stageNumber >= 6) {
      // GameScene.stageComplete() PAUSES GameScene + UIScene and launches
      // this scene on top of them. The next-stage branch below recycles both
      // through scene.start(GAME), but the boss branch starts a different
      // scene, which used to leave the paused stage-6 GameScene (with its
      // background videos) alive underneath the entire hydra fight. Stop
      // both here so the arena starts on a clean scene stack; BossScene
      // relaunches the UI itself with the boss HUD layout.
      this.scene.stop(SCENE_KEYS.GAME);
      this.scene.stop(SCENE_KEYS.UI);
      transitionTo(this, SCENE_KEYS.BOSS, { score: this.totalScore }, 500, 0x000000, 'parallaxPush');
    } else {
      transitionTo(this, SCENE_KEYS.GAME, { stage: this.stageNumber + 1, score: this.totalScore }, 500, 0x000000, 'parallaxPush');
    }
  }
}
